import { useState, useEffect, useMemo } from "react";
import { useCreate, useUpdate, useList } from "@refinedev/core";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/multi-select";
import { FormField, FormActions } from "@/components/forms";
import { notify } from '@/lib/notify';
import { translateErrorMessage } from "@/providers/notification-provider";
import type { TelegramBot, TelegramBotPayload } from "./types";

interface CountryItem {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
}

interface BotTokensFormProps {
  initialData?: TelegramBot | null;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * BotTokensForm
 *
 * Форма управления bot-tokens
 *
 * @returns {React.ReactElement} React компонент
 */
export function BotTokensForm({
  initialData,
  onSuccess,
  onCancel,
}: BotTokensFormProps) {
  const isEdit = !!initialData;

  const [name, setName] = useState(initialData?.name ?? "");
  const [token, setToken] = useState(initialData?.token ?? "");
  const [webhookUrl, setWebhookUrl] = useState(initialData?.webhookUrl ?? "");
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [allowedCountryIds, setAllowedCountryIds] = useState<number[]>(
    initialData?.allowedCountryIds ?? [],
  );

  // Update state when initialData changes
  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setToken(initialData.token);
      setWebhookUrl(initialData.webhookUrl ?? "");
      setIsActive(initialData.isActive);
      setAllowedCountryIds(initialData.allowedCountryIds ?? []);
    } else {
      setName("");
      setToken("");
      setWebhookUrl("");
      setIsActive(true);
      setAllowedCountryIds([]);
    }
  }, [initialData]);

  // Список активных стран для multi-select
  const countriesResult = useList<CountryItem>({
    resource: "admin/countries",
    pagination: { pageSize: 500, mode: "server" },
    filters: [{ field: "isActive", operator: "eq", value: true }],
  });
  const countryOptions = useMemo<MultiSelectOption[]>(
    () =>
      (countriesResult.query.data?.data ?? []).map((c) => ({
        value: String(c.id),
        label: `${c.code} — ${c.name}`,
      })),
    [countriesResult.query.data],
  );

  // Create mutation
  const { mutate: createMutation, mutation: createMut } = useCreate();
  const isCreating = createMut.isPending;

  // Update mutation
  const { mutate: updateMutation, mutation: updateMut } = useUpdate();
  const isUpdating = updateMut.isPending;

  const isPending = isCreating || isUpdating;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      notify.error("Название обязательно");
      return;
    }

    if (!isEdit && !token.trim()) {
      notify.error("Токен обязателен");
      return;
    }

    const trimmedToken = token.trim();
    const tokenPayload =
      trimmedToken && !/^\*+$/.test(trimmedToken) ? trimmedToken : undefined;

    const payload: TelegramBotPayload = {
      isActive,
      allowedCountryIds,
    };

    if (tokenPayload) {
      payload.token = tokenPayload;
    }

    if (webhookUrl.trim()) {
      payload.webhookUrl = webhookUrl.trim();
    } else {
      payload.webhookUrl = "";
    }

    if (isEdit) {
      updateMutation(
        {
          resource: "bot-tokens",
          id: initialData.id,
          values: payload,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
          onError: (error) => {
            notify.error(translateErrorMessage(error.message) || "Ошибка при обновлении бота");
          },
        }
      );
    } else {
      if (!tokenPayload) {
        notify.error("Токен обязателен");
        return;
      }

      payload.name = name.trim();
      payload.token = tokenPayload;
      createMutation(
        {
          resource: "bot-tokens",
          values: payload,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
          onError: (error) => {
            notify.error(translateErrorMessage(error.message) || "Ошибка при создании бота");
          },
        }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      {/*
        Hack to prevent browser autocomplete:
        Browsers often ignore autoComplete="off", but will fill the first password field they find
        and the preceding text field. We provide dummy hidden fields to trap them.
      */}
      <input type="text" className="hidden" autoComplete="off" />
      <input type="password" className="hidden" autoComplete="new-password" />

      <FormField label="Название" name="name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Например: Main Bot"
          disabled={isPending || isEdit}
          autoComplete="off"
        />
        {isEdit && (
          <p className="text-xs text-muted-foreground mt-1">
            Название нельзя изменить после создания
          </p>
        )}
      </FormField>

      <FormField
        label="Токен"
        name="token"
        required={!isEdit}
        hint={
          isEdit
            ? "Оставьте пустым, чтобы сохранить текущий токен"
            : "Токен бота от @BotFather"
        }
      >
        <Input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={isEdit ? "********" : "123456789:ABC-DEF..."}
          disabled={isPending}
          autoComplete="new-password"
        />
      </FormField>

      <FormField
        label="Webhook URL"
        name="webhookUrl"
        hint="Опционально. URL для получения обновлений"
      >
        <Input
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://example.com/webhook"
          disabled={isPending}
        />
      </FormField>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <label className="text-sm font-medium">Активен</label>
          <p className="text-sm text-muted-foreground">
            Бот будет запущен и обрабатывать сообщения
          </p>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={setIsActive}
          disabled={isPending}
        />
      </div>

      <FormField
        label="Доступные страны"
        name="allowedCountryIds"
        hint={
          allowedCountryIds.length === 0
            ? "Пусто — все активные страны. Юзер выбирает страну при онбординге."
            : allowedCountryIds.length === 1
              ? "Одна страна — шаг выбора в онбординге пропустится, юзеру автоматически проставится этот country и его авто-теги."
              : `Выбрано ${allowedCountryIds.length} — юзер выберет одну из них в онбординге.`
        }
      >
        <MultiSelect
          options={countryOptions}
          value={allowedCountryIds.map(String)}
          onChange={(values: string[]) => setAllowedCountryIds(values.map((v) => Number(v)))}
          placeholder={
            countriesResult.query.isLoading ? "Загрузка стран…" : "Все страны (по умолчанию)"
          }
          disabled={isPending || countriesResult.query.isLoading}
        />
      </FormField>

      <FormActions className="!mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Отмена
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Save className="mr-2 h-4 w-4" />
          {isEdit ? "Сохранить" : "Создать"}
        </Button>
      </FormActions>
    </form>
  );
}
